"use client";

import { useEffect, useState } from "react";
import { useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Form,
} from "@/components/ui/form";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { TagsService } from "@/services/TagService";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { TagIcon, Palette } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import type { TagsInput } from "@/interfaces";
import { schema } from "./schema/schema";

type TagFormValues = z.infer<typeof schema>;

const TagForm = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const tagsService = new TagsService();

  const form = useForm<TagFormValues, undefined, TagFormValues>({
    resolver: zodResolver(schema) as Resolver<TagFormValues, undefined, TagFormValues>,
    defaultValues: {
      name: "",
      styling: {
        fontColor: "#000000",
        primaryColor: "#E0E0E0",
        secondaryColor: "#BDBDBD",
      },
    },
  });

  useEffect(() => {
    const fetchTag = async () => {
      if (!id) return;

      try {
        setIsLoading(true);
        const tag = await tagsService.findById(id);
        if (tag) {
          form.reset({
            name: tag.name,
            styling: {
              fontColor: tag.styling?.fontColor || "#000000",
              primaryColor: tag.styling?.primaryColor || "#E0E0E0",
              secondaryColor: tag.styling?.secondaryColor || "#BDBDBD",
            },
          });
          setIsEditing(true);
        }
      } catch (error) {
        console.error("Erro ao buscar tag:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTag();
  }, [id, form]);

  const onSubmit = async (values: TagFormValues) => {
    try {
      setIsLoading(true);
      const tagInput: TagsInput = {
        name: values.name,
        styling: values.styling,
      };

      if (isEditing && id) {
        await tagsService.update(id, tagInput);
      } else {
        await tagsService.create(tagInput);
      }
      navigate("/tags");
    } catch (error) {
      console.error("Erro ao salvar tag:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-md mx-auto">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Card className="overflow-hidden border-none shadow-md">
            <CardHeader className="bg-gradient-to-r from-[#EE7B11] to-[#f59642] text-white">
              <div className="flex items-center gap-3">
                <TagIcon className="h-6 w-6" />
                <CardTitle className="text-xl">
                  {isEditing ? "Editar Tag" : "Nova Tag"}
                </CardTitle>
              </div>
            </CardHeader>

            {/* Preview fixo no topo do formulário */}
            <div className="p-4 bg-white border-b">
              <div className="flex flex-col items-center">
                <h4 className="text-sm font-medium text-gray-500 mb-2">
                  Pré-visualização
                </h4>
                <Badge
                  style={{
                    backgroundColor: form.watch("styling.primaryColor"),
                    color: form.watch("styling.fontColor"),
                    border: `1px solid ${form.watch("styling.secondaryColor")}`,
                  }}
                  className="rounded-md px-4 py-2 text-base"
                >
                  {form.watch("name") || "Nome da Tag"}
                </Badge>
              </div>
            </div>

            <CardContent className="p-4 sm:p-6 space-y-6">
              <div className="grid grid-cols-1 gap-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <TagIcon className="h-4 w-4 text-[#EE7B11]" />
                        Nome da Tag
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Digite o nome da tag"
                          {...field}
                          className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-500 mb-4 flex items-center gap-2">
                    <Palette className="h-4 w-4 text-[#EE7B11]" />
                    Estilização da Tag
                  </h3>

                  <div className="grid grid-cols-1 gap-4">
                    {/* Cor de Fundo */}
                    <FormField
                      control={form.control}
                      name="styling.primaryColor"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Cor de Fundo</FormLabel>
                          <div className="flex items-center gap-3">
                            <div
                              className="w-8 h-8 rounded border flex-shrink-0"
                              style={{ backgroundColor: field.value }}
                            ></div>
                            <FormControl>
                              <Input
                                type="color"
                                {...field}
                                className="w-12 h-8 p-0 border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                              />
                            </FormControl>
                            <FormControl>
                              <Input
                                type="text"
                                value={field.value}
                                onChange={(e) => field.onChange(e.target.value)}
                                className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11] w-28"
                                placeholder="#000000"
                              />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Cor da Borda */}
                    <FormField
                      control={form.control}
                      name="styling.secondaryColor"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Cor da Borda</FormLabel>
                          <div className="flex items-center gap-3">
                            <div
                              className="w-8 h-8 rounded border flex-shrink-0"
                              style={{ backgroundColor: field.value }}
                            ></div>
                            <FormControl>
                              <Input
                                type="color"
                                {...field}
                                className="w-12 h-8 p-0 border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                              />
                            </FormControl>
                            <FormControl>
                              <Input
                                type="text"
                                value={field.value}
                                onChange={(e) => field.onChange(e.target.value)}
                                className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11] w-28"
                                placeholder="#000000"
                              />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Cor do Texto */}
                    <FormField
                      control={form.control}
                      name="styling.fontColor"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Cor do Texto</FormLabel>
                          <div className="flex items-center gap-3">
                            <div
                              className="w-8 h-8 rounded border flex-shrink-0"
                              style={{ backgroundColor: field.value }}
                            ></div>
                            <FormControl>
                              <Input
                                type="color"
                                {...field}
                                className="w-12 h-8 p-0 border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                              />
                            </FormControl>
                            <FormControl>
                              <Input
                                type="text"
                                value={field.value}
                                onChange={(e) => field.onChange(e.target.value)}
                                className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11] w-28"
                                placeholder="#000000"
                              />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter className="border-t p-4 sm:p-6 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/tags")}
                className="border-gray-200 text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
              >
                {isEditing ? "Atualizar" : "Salvar"}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </Form>
    </div>
  );
};

export default TagForm;







